import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import * as XLSX from 'xlsx';
import DataList from './DataList';
import { useAuth } from '../hooks/useAuth';
import '../styles/NassitSchedule.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const currentPayPeriod = () => {
  const now = new Date();
  return `${now.getFullYear()} ${MONTH_NAMES[now.getMonth()]}`;
};

const buildPayPeriods = () => {
  const startYear = 2024;
  const endYear = new Date().getFullYear() + 1;
  const periods = [];
  for (let year = startYear; year <= endYear; year += 1) {
    MONTH_NAMES.forEach((month) => {
      periods.push(`${year} ${month}`);
    });
  }
  return periods;
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todayDateInput = () => new Date().toISOString().slice(0, 10);

const SCHEDULE_URL = `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/nassit-schedule`;
const HISTORY_URL = `${SCHEDULE_URL}/history`;

const SCREEN_LIST = 'list';
const SCREEN_MONTH = 'month';
const SCREEN_HELPER = 'helper';

const STEP_CHECK = 1;
const STEP_SAVE = 2;
const STEP_REVIEW = 3;
const STEP_PAY = 4;
const STEP_DONE = 5;

const STEP_LABELS = ['Check the list', 'Save the list', 'Record payment'];

const PROOF_SLOTS = [
  { kind: 'stamped_submission', label: 'Stamped Submission' },
  { kind: 'payment_advice', label: 'Payment Advice' },
  { kind: 'payment_receipt', label: 'Payment Receipt' },
];
const PROOF_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const PROOF_BUCKET = 'sh-employees';
const PROOF_FOLDER = 'nassit';
const PROOF_MAX_BYTES = 10 * 1024 * 1024;
const PROOF_MIME_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);
const DOCUMENTS_URL = `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/documents`;
const DOWNLOAD_URL = `${DOCUMENTS_URL}/download`;
const UPLOAD_URL = `${DOCUMENTS_URL}/lab-results/upload-pdf`;
const DELETE_FILE_URL = `${DOCUMENTS_URL}/lab-results/delete-pdf`;

const fileNameFromPath = (storedPath) => {
  const name = String(storedPath || '').split('/').pop() || 'file';
  return name.length > 40 ? `${name.slice(0, 40)}...` : name;
};

const requestErrorMessage = (requestError, fallback) =>
  requestError?.response?.data?.error || fallback;

const NassitSchedule = () => {
  const { hasPermission } = useAuth();
  const canView = hasPermission('/nassit-schedule', 'View');
  const canExport = hasPermission('/nassit-schedule', 'Export');
  const canSave = hasPermission('/nassit-schedule', 'Add');
  const canRecordPayment = hasPermission('/nassit-schedule', 'Edit');
  const canUpdateList = canSave || canRecordPayment;

  const [screen, setScreen] = useState(SCREEN_LIST);
  const [listKey, setListKey] = useState(0);
  const [step, setStep] = useState(STEP_CHECK);
  const [payPeriod, setPayPeriod] = useState(currentPayPeriod);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [confirmIncomplete, setConfirmIncomplete] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [isUpdatingList, setIsUpdatingList] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [uploadingKind, setUploadingKind] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [remittance, setRemittance] = useState({
    remittance_date: todayDateInput(),
    remittance_amount: '',
    ss4a_reference: '',
    ss4b_reference: '',
    notes: '',
  });

  const applySchedule = (nextSchedule) => {
    setSchedule(nextSchedule);
    const record = nextSchedule?.record;
    const defaultAmount = record?.remittance_amount
      ?? record?.totals?.total_contribution
      ?? nextSchedule?.totals?.total_contribution
      ?? '';
    setRemittance({
      remittance_date: record?.remittance_date || todayDateInput(),
      remittance_amount: defaultAmount === '' || defaultAmount == null ? '' : defaultAmount,
      ss4a_reference: record?.ss4a_reference || '',
      ss4b_reference: record?.ss4b_reference || '',
      notes: record?.notes || '',
    });
  };

  const goToList = () => {
    setScreen(SCREEN_LIST);
    setListKey((key) => key + 1);
    setError('');
    setMessage('');
    setConfirmIncomplete(false);
    setShowMoreDetails(false);
    setIsUpdatingList(false);
    setSelectedEmployeeId('');
    setSchedule(null);
  };

  const fetchSchedule = useCallback(async (period) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.get(SCHEDULE_URL, {
        params: { pay_period: period },
      });
      applySchedule(response.data);
      return response.data;
    } catch (fetchError) {
      console.error('Error fetching NASSIT schedule:', fetchError);
      setError('Could not load this month. Try again.');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const openHelper = async (period, preferredStep) => {
    setPayPeriod(period);
    setConfirmIncomplete(false);
    setShowMoreDetails(false);
    setIsUpdatingList(false);
    setSelectedEmployeeId('');
    setMessage('');
    const nextSchedule = await fetchSchedule(period);
    if (!nextSchedule) return;

    const isRemitted = nextSchedule.record?.status === 'remitted';
    const isSaved = nextSchedule.source === 'saved';
    let nextStep = preferredStep;
    if (isRemitted) nextStep = STEP_DONE;
    else if (preferredStep == null && isSaved) nextStep = STEP_REVIEW;
    else if (preferredStep == null) nextStep = STEP_CHECK;
    setStep(nextStep);
    setScreen(SCREEN_HELPER);
  };

  const startCreate = async () => {
    setError('');
    setMessage('');
    try {
      const response = await axios.get(HISTORY_URL);
      const used = new Set((response.data || []).map((row) => row.pay_period));
      const openMonths = buildPayPeriods().filter((period) => !used.has(period));
      const defaultMonth = openMonths.includes(currentPayPeriod())
        ? currentPayPeriod()
        : (openMonths[openMonths.length - 1] || currentPayPeriod());
      setAvailableMonths(openMonths);
      setPayPeriod(defaultMonth);
      setScreen(SCREEN_MONTH);
    } catch (historyError) {
      console.error('Error loading NASSIT history:', historyError);
      setError('Could not start a new submission. Try again.');
    }
  };

  const continueFromMonth = async () => {
    if (!payPeriod) return;
    try {
      const response = await axios.get(HISTORY_URL);
      const existing = (response.data || []).find((row) => row.pay_period === payPeriod);
      if (existing) {
        await openHelper(payPeriod);
        return;
      }
    } catch (historyError) {
      console.error('Error checking existing month:', historyError);
    }
    await openHelper(payPeriod, STEP_CHECK);
  };

  const handleExport = () => {
    if (!schedule?.rows?.length) return;

    const headerRows = [
      ['Name of Establishment', schedule.employer?.name || ''],
      ['Employer Registration Number', schedule.employer?.registration_number || ''],
      ['Month', payPeriod],
      [],
      ['Name of Employee', 'Social Security Number', 'Salary', "Employee's Contribution (5%)", "Employer's Contribution (10%)", 'Total Contributions (15%)'],
    ];

    const dataRows = schedule.rows.map((row) => ([
      row.employee_name,
      row.nassit_ssn,
      row.taxable_amount,
      row.employee_share,
      row.employer_share,
      row.total_contribution,
    ]));

    const totals = schedule.totals || {};
    dataRows.push([
      'TOTAL',
      '',
      totals.taxable_amount,
      totals.employee_share,
      totals.employer_share,
      totals.total_contribution,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([...headerRows, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'NASSIT Schedule');
    const filePeriod = String(payPeriod || 'schedule').replace(/\s+/g, '_');
    XLSX.writeFile(workbook, `nassit_schedule_${filePeriod}.xlsx`);
  };

  const startUpdateList = async () => {
    if (isRemittedStatus()) return;
    setError('');
    setMessage('');
    setIsUpdatingList(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/all`
      );
      const rows = Array.isArray(response.data) ? response.data : [];
      setStaffOptions(rows.filter((employee) => {
        const basePay = Number(employee.base_pay);
        return (employee.status === true || employee.status === 1)
          && !employee.nassit_exempt
          && Number.isFinite(basePay)
          && basePay > 0;
      }));
    } catch (staffError) {
      console.error('Error loading employees:', staffError);
      setError('Could not load staff to add. Try again.');
    }
  };

  const isRemittedStatus = () => schedule?.record?.status === 'remitted';

  const handleAddEmployee = async () => {
    const recordId = schedule?.record?.id;
    if (!recordId || !selectedEmployeeId) {
      setError('Choose a person to add.');
      return;
    }
    setIsAdding(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.post(`${SCHEDULE_URL}/${recordId}/employees`, {
        employee_id: selectedEmployeeId,
      });
      applySchedule(response.data);
      setSelectedEmployeeId('');
      setMessage('That person was added to this list.');
    } catch (addError) {
      console.error('Error adding employee to NASSIT list:', addError);
      setError(requestErrorMessage(addError, 'Could not add this person. Try again.'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveEmployee = async (row) => {
    const recordId = schedule?.record?.id;
    if (!recordId || !row?.employee_id) return;
    setError('');
    setMessage('');
    try {
      const response = await axios.delete(`${SCHEDULE_URL}/${recordId}/employees/${row.employee_id}`);
      applySchedule(response.data);
      setMessage(`${row.employee_name} was removed from this list.`);
    } catch (removeError) {
      console.error('Error removing employee from NASSIT list:', removeError);
      setError(requestErrorMessage(removeError, 'Could not remove this person. Try again.'));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.post(SCHEDULE_URL, { pay_period: payPeriod });
      applySchedule(response.data);
      setStep(STEP_REVIEW);
      setMessage(`The list for ${payPeriod} is saved.`);
    } catch (saveError) {
      console.error('Error saving NASSIT schedule:', saveError);
      setError(requestErrorMessage(saveError, 'Could not save this month. Try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecordPayment = async (event) => {
    event.preventDefault();
    const recordId = schedule?.record?.id;
    if (!recordId) return;

    setIsRecording(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.put(`${SCHEDULE_URL}/${recordId}/remittance`, remittance);
      applySchedule(response.data);
      setStep(STEP_DONE);
      setMessage(`Payment recorded for ${payPeriod}.`);
    } catch (recordError) {
      console.error('Error recording NASSIT payment:', recordError);
      setError(requestErrorMessage(recordError, 'Could not save the payment. Try again.'));
    } finally {
      setIsRecording(false);
    }
  };

  const handleUploadProof = async (kind, file) => {
    const recordId = schedule?.record?.id;
    if (!recordId || !file || !canRecordPayment) return;

    if (file.size > PROOF_MAX_BYTES) {
      setError('File is too large. Maximum size is 10 MB.');
      return;
    }
    if (file.type && !PROOF_MIME_TYPES.has(file.type)) {
      setError('Use a PDF, JPG, or PNG file.');
      return;
    }

    setUploadingKind(kind);
    setUploadProgress(0);
    setError('');
    setMessage('');
    const previousPath = schedule?.record?.proof?.[kind] || '';
    const payload = new FormData();
    payload.append('file', file);
    payload.append('filePath', `${PROOF_BUCKET}/${PROOF_FOLDER}/${recordId}/${kind}`);
    payload.append('id', recordId);
    payload.append('columnName', kind);
    try {
      const uploaded = await axios.post(UPLOAD_URL, payload, {
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        },
      });
      const storedPath = uploaded.data?.path;
      if (!storedPath) {
        setError('Could not upload this file. Try again.');
        return;
      }
      const response = await axios.post(`${SCHEDULE_URL}/${recordId}/proof`, {
        kind,
        path: storedPath,
      });
      applySchedule(response.data);
      if (previousPath && previousPath !== storedPath) {
        await axios.post(DELETE_FILE_URL, {
          gcPath: previousPath,
          columnName: kind,
          id: recordId,
        }).catch((deleteError) => {
          console.error('Error removing the previous NASSIT proof file:', deleteError);
        });
      }
      setMessage('File saved.');
    } catch (uploadError) {
      console.error('Error uploading NASSIT proof file:', uploadError);
      setError(requestErrorMessage(uploadError, 'Could not upload this file. Try again.'));
    } finally {
      setUploadingKind('');
      setUploadProgress(0);
    }
  };

  const handleRemoveProof = async (kind) => {
    const recordId = schedule?.record?.id;
    if (!recordId || !canRecordPayment) return;
    if (!window.confirm('Remove this file?')) return;

    const storedPath = schedule?.record?.proof?.[kind] || '';
    setUploadingKind(kind);
    setError('');
    setMessage('');
    try {
      if (storedPath) {
        await axios.post(DELETE_FILE_URL, {
          gcPath: storedPath,
          columnName: kind,
          id: recordId,
        });
      }
      const response = await axios.delete(`${SCHEDULE_URL}/${recordId}/proof/${kind}`);
      applySchedule(response.data);
      setMessage('File removed.');
    } catch (removeError) {
      console.error('Error removing NASSIT proof file:', removeError);
      setError(requestErrorMessage(removeError, 'Could not remove this file. Try again.'));
    } finally {
      setUploadingKind('');
    }
  };

  const handleDownloadProof = async (storedPath) => {
    if (!storedPath) return;
    setError('');
    try {
      const response = await axios.get(DOWNLOAD_URL, {
        params: { filePath: storedPath },
        responseType: 'blob',
      });
      const fileName = response.headers['content-disposition']
        ? response.headers['content-disposition'].split('filename=')[1]?.replace(/['"]/g, '')
        : storedPath.split('/').pop();
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], {
        type: response.headers['content-type'],
      }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'file';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (downloadError) {
      console.error('Error downloading NASSIT proof file:', downloadError);
      setError('Could not download this file. Try again.');
    }
  };

  const continueFromCheck = () => {
    const problems = schedule?.problems || [];
    if (problems.length && !confirmIncomplete) {
      setConfirmIncomplete(true);
      return;
    }
    if (canSave && schedule?.source !== 'saved') {
      setStep(STEP_SAVE);
      return;
    }
    setStep(STEP_REVIEW);
  };

  if (!canView) {
    return (
      <div className="nassit-schedule-container">
        <h2>NASSIT Schedule</h2>
        <p>You do not have permission to view the NASSIT schedule.</p>
      </div>
    );
  }

  const columns = [
    { name: 'Employee', selector: (row) => row.employee_name, sortable: true, grow: 2 },
    { name: 'NASSIT Number', selector: (row) => row.nassit_ssn, sortable: true, grow: 2 },
    { name: 'Branch', selector: (row) => row.branch, sortable: true },
    { name: 'Taxable Amount', selector: (row) => formatMoney(row.taxable_amount), sortable: true, right: true },
    { name: 'Employee 5%', selector: (row) => formatMoney(row.employee_share), sortable: true, right: true },
    { name: 'Employer 10%', selector: (row) => formatMoney(row.employer_share), sortable: true, right: true },
    { name: 'Total 15%', selector: (row) => formatMoney(row.total_contribution), sortable: true, right: true },
  ];

  const listColumns = [
    { id: 'period_date', name: 'Month', selector: (row) => row.pay_period, sortable: true, filterable: true, filterSelector: 'pay_period', sortFunction: (a, b) => String(a.period_date || '').localeCompare(String(b.period_date || '')) },
    { name: 'Status', selector: (row) => row.status_label, sortable: true, filterable: true, filterSelector: 'status_label' },
    { name: 'Proof', selector: (row) => row.proof_label, sortable: true },
    { name: 'Staff on list', selector: (row) => row.contributing_count, sortable: true },
    {
      name: 'Amount to pay',
      selector: (row) => Number(row.total_contribution || 0),
      sortable: true,
      cell: (row) => formatMoney(row.total_contribution),
      right: true,
    },
    { id: 'remittance_date', name: 'Paid on', selector: (row) => row.remittance_date, sortable: true, type: 'date' },
    { name: 'ID', selector: (row) => row.schedule_id, identifier: true, omit: true },
  ];

  const listConfig = {
    title: 'NASSIT Submissions',
    path: '/nassit-schedule',
    fetchEndpoint: '/hospital-portal/api/employees/nassit-schedule/history',
    defaultSortFieldId: 'period_date',
    defaultSortAsc: false,
    allowAdd: canSave,
    allowEdit: canRecordPayment,
    allowExport: false,
    addButtonLabel: 'Create New Submission',
    expandableRows: 'Off',
    onAdd: startCreate,
    onEdit: (record) => openHelper(record.pay_period),
    onView: (record) => openHelper(record.pay_period),
    conditionalStyles: {
      Status: {
        type: 'text',
        conditions: {
          matches: {
            'Saved — not paid yet': { color: 'black', backgroundColor: 'yellow' },
            'Paid — proof missing': { color: 'black', backgroundColor: '#ffcc80' },
            Complete: { color: 'white', backgroundColor: 'green' },
          },
          default: { color: 'black', backgroundColor: 'white' },
        },
      },
    },
  };

  if (screen === SCREEN_LIST) {
    return (
      <div className="nassit-schedule-list">
        {error && <div className="nassit-schedule-error">{error}</div>}
        <DataList key={listKey} apiConfig={listConfig} columns={listColumns} />
      </div>
    );
  }

  if (screen === SCREEN_MONTH) {
    return (
      <div className="nassit-schedule-container nassit-helper">
        <button type="button" className="nassit-back-link" onClick={goToList}>Back to list</button>
        <h2>Create New Submission</h2>
        <p className="nassit-schedule-help">Which month is this NASSIT list for?</p>
        {availableMonths.length === 0 ? (
          <p>Every month already has a saved list. Open one from the list instead.</p>
        ) : (
          <>
            <label className="nassit-month-picker">
              Month
              <select value={payPeriod} onChange={(e) => setPayPeriod(e.target.value)}>
                {availableMonths.map((period) => (
                  <option key={period} value={period}>{period}</option>
                ))}
              </select>
            </label>
            <button type="button" className="nassit-primary-button" onClick={continueFromMonth}>
              Continue
            </button>
          </>
        )}
      </div>
    );
  }

  const totals = schedule?.totals || {};
  const problems = schedule?.problems || [];
  const record = schedule?.record;
  const isSaved = schedule?.source === 'saved';
  const isRemitted = record?.status === 'remitted';
  const fullMonthTotal = record?.totals?.total_contribution ?? totals.total_contribution;
  const visibleStep = isRemitted ? STEP_DONE : step;
  const peopleOnList = new Set((schedule?.rows || []).map((row) => String(row.employee_id)));
  const peopleToAdd = staffOptions.filter((employee) => !peopleOnList.has(String(employee.employee_id)));
  const showProof = isSaved && (
    visibleStep === STEP_REVIEW || visibleStep === STEP_PAY || visibleStep === STEP_DONE
  );
  const proofPaths = record?.proof || {};
  const proofPanel = showProof ? (
    <section className="nassit-proof">
      <h3>Proof of payment</h3>
      <p className="nassit-schedule-help">
        Upload these three papers. This month is finished only when the payment is saved and all three are here.
      </p>
      {PROOF_SLOTS.map((slot) => {
        const storedPath = proofPaths[slot.kind] || '';
        const isBusy = uploadingKind === slot.kind;
        const fileInput = (
          <input
            type="file"
            accept={PROOF_ACCEPT}
            disabled={Boolean(uploadingKind)}
            onChange={(event) => {
              const nextFile = event.target.files?.[0];
              event.target.value = '';
              if (nextFile) handleUploadProof(slot.kind, nextFile);
            }}
          />
        );
        return (
          <div className="nassit-proof-row" key={slot.kind}>
            <strong>{slot.label}</strong>
            {storedPath ? (
              <div className="nassit-proof-actions">
                <button type="button" className="nassit-back-link" onClick={() => handleDownloadProof(storedPath)}>
                  {fileNameFromPath(storedPath)}
                </button>
                {canRecordPayment && (
                  <>
                    <label className="nassit-secondary-button nassit-file-button">
                      {isBusy ? `Uploading ${uploadProgress}%` : 'Replace'}
                      {fileInput}
                    </label>
                    <button
                      type="button"
                      className="nassit-back-link"
                      onClick={() => handleRemoveProof(slot.kind)}
                      disabled={Boolean(uploadingKind)}
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="nassit-proof-actions">
                {canRecordPayment ? (
                  <label className="nassit-primary-button nassit-file-button">
                    {isBusy ? `Uploading ${uploadProgress}%` : 'Choose file'}
                    {fileInput}
                  </label>
                ) : (
                  <span>Not uploaded yet</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </section>
  ) : null;

  return (
    <div className="nassit-schedule-container nassit-helper">
      <button type="button" className="nassit-back-link" onClick={goToList}>Back to list</button>
      <h2>NASSIT list for {payPeriod}</h2>

      <ol className="nassit-stepper">
        {STEP_LABELS.map((label, index) => {
          const number = index + 1;
          const guideStep = visibleStep === STEP_CHECK ? 1
            : visibleStep === STEP_SAVE ? 2
            : 3;
          const isCurrent = visibleStep !== STEP_DONE && guideStep === number;
          const isComplete = visibleStep === STEP_DONE || number < guideStep;
          return (
            <li
              key={label}
              className={`nassit-stepper-item${isCurrent ? ' is-current' : ''}${isComplete ? ' is-complete' : ''}`}
            >
              <span className="nassit-stepper-number">{number}</span>
              <span>{label}</span>
            </li>
          );
        })}
      </ol>

      {error && <div className="nassit-schedule-error">{error}</div>}
      {message && <div className="nassit-schedule-success">{message}</div>}

      {visibleStep === STEP_CHECK && (
        <>
          <p className="nassit-schedule-help">
            These staff will be on the NASSIT payment for {payPeriod}.
          </p>
          <div className="nassit-totals">
            <div><strong>{schedule?.contributing_count || 0}</strong><span>staff</span></div>
            <div><strong>{formatMoney(totals.total_contribution)}</strong><span>amount to pay</span></div>
          </div>
          {problems.length > 0 && (
            <div className="nassit-schedule-problems">
              <strong>{problems.length} staff still need NASSIT details.</strong>
              {' '}
              <Link to="/employee-list">Open Employee List to fix them.</Link>
              <ul>
                {problems.slice(0, 8).map((problem) => (
                  <li key={problem.employee_id}>
                    {problem.employee_name}: {problem.issues.join('; ')}
                  </li>
                ))}
              </ul>
              {problems.length > 8 && <p>And {problems.length - 8} more.</p>}
            </div>
          )}
          {confirmIncomplete && (
            <div className="nassit-schedule-live">
              Some records are incomplete. Continue anyway?
            </div>
          )}
          {!schedule?.rows?.length && (
            <p>No contributing staff yet. Add base pay on the Employee List first.</p>
          )}
          <button
            type="button"
            className="nassit-primary-button"
            onClick={continueFromCheck}
            disabled={isLoading || !schedule?.rows?.length}
          >
            {confirmIncomplete ? 'Continue anyway' : 'Looks good — continue'}
          </button>
        </>
      )}

      {visibleStep === STEP_SAVE && (
        <>
          <p className="nassit-schedule-help">
            Save this list for {payPeriod} so it will not change later. Amount to pay: {formatMoney(totals.total_contribution)}.
          </p>
          {problems.length > 0 && (
            <div className="nassit-schedule-live">
              Some staff still have missing details. You can save this list now and fix them later.
            </div>
          )}
          <button
            type="button"
            className="nassit-primary-button"
            onClick={handleSave}
            disabled={isSaving || !schedule?.rows?.length}
          >
            {isSaving ? 'Saving...' : 'Save this list'}
          </button>
        </>
      )}

      {visibleStep === STEP_REVIEW && isSaved && !isRemitted && (
        <div className="nassit-schedule-saved">
          This list is saved. You can update it, record the payment, or download the Excel file.
        </div>
      )}

      {visibleStep === STEP_PAY && canRecordPayment && (
        <form className="nassit-remittance-form" onSubmit={handleRecordPayment}>
          <p className="nassit-schedule-help">
            After NASSIT is paid, enter the payment here. Full-month total is {formatMoney(fullMonthTotal)}.
          </p>
          <div className="nassit-remittance-grid">
            <label>
              Payment date
              <input
                type="date"
                value={remittance.remittance_date}
                onChange={(e) => setRemittance((prev) => ({ ...prev, remittance_date: e.target.value }))}
                required
              />
            </label>
            <label>
              Amount paid
              <input
                type="number"
                min="0"
                step="0.01"
                value={remittance.remittance_amount}
                onChange={(e) => setRemittance((prev) => ({ ...prev, remittance_amount: e.target.value }))}
              />
            </label>
            <label>
              NASSIT receipt / SS4A number
              <input
                type="text"
                value={remittance.ss4a_reference}
                onChange={(e) => setRemittance((prev) => ({ ...prev, ss4a_reference: e.target.value }))}
              />
            </label>
            {showMoreDetails && (
              <>
                <label>
                  SS4B number
                  <input
                    type="text"
                    value={remittance.ss4b_reference}
                    onChange={(e) => setRemittance((prev) => ({ ...prev, ss4b_reference: e.target.value }))}
                  />
                </label>
                <label className="nassit-remittance-notes">
                  Notes
                  <input
                    type="text"
                    value={remittance.notes}
                    onChange={(e) => setRemittance((prev) => ({ ...prev, notes: e.target.value }))}
                  />
                </label>
              </>
            )}
          </div>
          {!showMoreDetails && (
            <button type="button" className="nassit-back-link" onClick={() => setShowMoreDetails(true)}>
              More details
            </button>
          )}
          {proofPanel}
          <div className="nassit-helper-actions">
            <button type="submit" className="nassit-primary-button" disabled={isRecording}>
              {isRecording ? 'Saving...' : 'Save payment'}
            </button>
            <button type="button" className="nassit-secondary-button" onClick={goToList}>
              I will do this later
            </button>
          </div>
        </form>
      )}

      {visibleStep === STEP_DONE && (
        <div className={`nassit-schedule-remitted${record?.proof_complete ? '' : ' is-incomplete'}`}>
          {record?.proof_complete ? (
            <p><strong>{payPeriod} is finished.</strong></p>
          ) : (
            <p>
              <strong>Payment for {payPeriod} is saved.</strong>
              {' '}
              Upload the missing papers. This month is finished only when all three are here.
            </p>
          )}
          {record?.remittance_date && <p>Paid on {record.remittance_date}.</p>}
          {record?.remittance_amount != null && <p>Amount: {formatMoney(record.remittance_amount)}.</p>}
          {record?.ss4a_reference ? <p>SS4A: {record.ss4a_reference}.</p> : null}
        </div>
      )}

      {visibleStep !== STEP_PAY && (
        <div className="data-table-container">
          {(visibleStep === STEP_REVIEW || visibleStep === STEP_DONE) && (
            <div className="nassit-list-actions">
              <button
                type="button"
                className="nassit-secondary-button"
                onClick={startUpdateList}
                disabled={isRemitted || !canUpdateList || isLoading}
              >
                Edit the List
              </button>
              {canRecordPayment && (
                <button
                  type="button"
                  className="nassit-primary-button"
                  onClick={() => setStep(STEP_PAY)}
                  disabled={isRemitted}
                >
                  Record Payment
                </button>
              )}
              {canExport && (
                <button
                  type="button"
                  className="nassit-secondary-button"
                  onClick={handleExport}
                  disabled={isLoading || !schedule?.rows?.length}
                >
                  Download Excel File
                </button>
              )}
            </div>
          )}
          {visibleStep !== STEP_PAY && proofPanel}
          {isUpdatingList && !isRemitted && (
            <div className="nassit-add-staff">
              <p className="nassit-schedule-help">
                You can only add people who already have a base pay on the Employee List.
                {' '}
                Amounts cannot be typed here.
                {' '}
                <Link to="/employee-list">Open Employee List</Link>
                {' '}to set base pay first.
              </p>
              {peopleToAdd.length > 0 ? (
                <div className="nassit-add-staff-row">
                  <label>
                    Staff
                    <select
                      value={selectedEmployeeId}
                      onChange={(e) => setSelectedEmployeeId(e.target.value)}
                    >
                      <option value="">Choose a person</option>
                      {peopleToAdd.map((employee) => (
                        <option key={employee.employee_id} value={employee.employee_id}>
                          {employee.employee_name} — {formatMoney(employee.base_pay)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="nassit-primary-button"
                    onClick={handleAddEmployee}
                    disabled={isAdding || !selectedEmployeeId}
                  >
                    {isAdding ? 'Adding...' : 'Add to this list'}
                  </button>
                </div>
              ) : (
                <p className="nassit-schedule-help">
                  {staffOptions.length === 0
                    ? 'No staff have a base pay yet. Set it on the Employee List first.'
                    : 'Everyone with a base pay is already on this list.'}
                </p>
              )}
            </div>
          )}
          <div className="summary-row">
            <strong>Staff: {schedule?.contributing_count || 0}</strong>
            {' | '}
            <strong>Total 15%: {formatMoney(totals.total_contribution)}</strong>
          </div>
          <DataTable
            columns={[
              ...columns,
              ...(isUpdatingList && canUpdateList && !isRemitted ? [{
                name: '',
                ignoreRowClick: true,
                width: '90px',
                cell: (row) => (
                  <button type="button" className="nassit-back-link" onClick={() => handleRemoveEmployee(row)}>
                    Remove
                  </button>
                ),
              }] : []),
            ]}
            data={schedule?.rows || []}
            pagination
            paginationPerPage={30}
            highlightOnHover
            progressPending={isLoading}
            noDataComponent="No contributing employees. Add a person to this list."
          />
        </div>
      )}
    </div>
  );
};

export default NassitSchedule;
